// SNIGJ - Notification Center Component (real-time polling)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bell, Check, CheckCheck, AlertTriangle, Shield, Users, Gavel, Unlock, FileText, X, Clock } from 'lucide-react';

const POLL_INTERVAL = 30000; // 30 seconds

const ICON_MAP = {
  GARDE_A_VUE: { icon: Shield, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
  INTERPELLATION: { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  INJONCTION: { icon: Gavel, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  LIBERATION_DIRECTE: { icon: Unlock, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
  INJONCTION_ACKNOWLEDGED: { icon: Check, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  CONNEXION_SUSPECTE: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
  DEFAULT: { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-100 dark:bg-gray-900/30' }
};

const NotificationCenter = () => {
  const { user, language } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const prevCountRef = useRef(0);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/count');
      const newCount = res.data.unread_count || 0;
      // Play sound if new notifications arrived (only after user interaction)
      if (newCount > prevCountRef.current && prevCountRef.current >= 0 && document.hasFocus()) {
        // Browser requires user interaction before audio can play - silently skip if not allowed
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (err) {
      // silent
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Poll for unread count
  useEffect(() => {
    if (!user) return;
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [user, fetchCount]);

  // Load full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const markAsRead = async (notifId) => {
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {}
  };

  const getIcon = (type) => {
    const config = ICON_MAP[type] || ICON_MAP.DEFAULT;
    return config;
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return language === 'fr' ? "à l'instant" : 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative w-8 h-8 sm:w-9 sm:h-9"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notifications-btn"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border rounded-lg shadow-xl z-50 max-h-[70vh] flex flex-col" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
              {unreadCount > 0 && <Badge className="bg-red-500 text-white text-[10px]">{unreadCount}</Badge>}
            </h3>
            <div className="flex gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7 px-2" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3 h-3 mr-1" />
                  {language === 'fr' ? 'Tout lire' : 'Read all'}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsOpen(false)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto flex-1">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {language === 'fr' ? 'Chargement...' : 'Loading...'}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                {language === 'fr' ? 'Aucune notification' : 'No notifications'}
              </div>
            ) : (
              notifications.map((notif) => {
                const iconConfig = getIcon(notif.notification_type);
                const IconComp = iconConfig.icon;
                return (
                  <div
                    key={notif.notification_id}
                    className={`px-4 py-3 border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors ${!notif.is_read ? 'bg-primary/5' : ''}`}
                    onClick={() => !notif.is_read && markAsRead(notif.notification_id)}
                    data-testid={`notif-${notif.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full ${iconConfig.bg} flex items-center justify-center shrink-0`}>
                        <IconComp className={`w-4 h-4 ${iconConfig.color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${!notif.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {notif.title}
                          </p>
                          {notif.priority === 'URGENT' && (
                            <Badge className="bg-red-500 text-white text-[9px] px-1 shrink-0">URGENT</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />
                            {timeAgo(notif.created_at)}
                          </span>
                          {!notif.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
